import { useEffect, useState } from "react";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { listPricingTiers } from "@/integrations/backend/pricing";

type Billing = "monthly" | "annual";

type Tier = {
  id: string;
  name: string;
  blurb: string;
  features: string[];
  price_monthly: number;
  price_annual: number;
  highlight: boolean;
  sort_order: number;
};

export default function PricingPage() {
  const [billing, setBilling] = useState<Billing>("monthly");
  const [tiers, setTiers] = useState<Tier[]>([]);
  useEffect(() => {
    (async () => {
      try {
        const rows = await listPricingTiers();
        setTiers(rows as any);
      } catch {
        setTiers([]);
      }
    })();
  }, []);
  const hero = "https://xnjrgbjrqhbqsfocowbt.supabase.co/storage/v1/object/public/marketing/meghan-holmes-wy_L8W0zcpI-unsplash.jpg";

  return (
    <>
      <MarketingHeader />
      <main className="min-h-screen gradient-dark pt-24">
        {/* Hero */}
        <section className="relative min-h-[45vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img src={hero} alt="Pricing hero" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-dark-bg/95 via-dark-bg/80 to-dark-bg" />
          </div>
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/25 to-transparent" style={{ left: 'calc(50% - 220px)' }} />
            <div className="absolute top-0 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent" style={{ left: 'calc(50% + 220px)' }} />
          </div>
          <div className="container mx-auto px-6 text-center relative z-10">
            <h1 className="text-5xl md:text-6xl font-black text-white leading-tight">Pricing</h1>
            <p className="text-gray-light text-xl md:text-2xl mt-3">Train smarter with a plan that fits your journey.</p>
          </div>
        </section>

        {/* Toggle */}
        <section className="py-8">
          <div className="container mx-auto px-6 text-center">
            <div className="inline-flex items-center bg-dark-card/70 backdrop-blur-sm border border-primary/20 rounded-full p-1">
              <button
                className={`px-6 py-2 rounded-full text-sm font-semibold transition ${billing === 'monthly' ? 'bg-primary text-black' : 'text-gray-light'}`}
                onClick={()=>setBilling('monthly')}
              >
                Monthly
              </button>
              <button
                className={`px-6 py-2 rounded-full text-sm font-semibold transition ${billing === 'annual' ? 'bg-primary text-black' : 'text-gray-light'}`}
                onClick={()=>setBilling('annual')}
              >
                Annual <span className="opacity-80">(save 20%)</span>
              </button>
            </div>
          </div>
        </section>

        {/* Tiers */}
        <section className="py-12">
          <div className="container mx-auto px-6 grid md:grid-cols-3 gap-8 max-w-7xl">
            {tiers.map((t) => (
              <div key={t.name} className={`relative rounded-2xl border ${t.highlight ? 'border-primary/50 shadow-neon-glow-strong' : 'border-primary/20 shadow-neon-glow'} bg-dark-card/80 backdrop-blur-sm p-8 flex flex-col`}>
                {t.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-black text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">Most Popular</div>
                )}
                <h3 className="text-2xl font-bold text-white">{t.name}</h3>
                <p className="text-gray-light mt-2">{t.blurb}</p>
                <div className="mt-6">
                  <span className="text-5xl font-black text-white">${billing === 'monthly' ? t.price_monthly : t.price_annual}</span>
                  <span className="text-gray-light ml-2">/ {billing}</span>
                </div>
                <ul className="mt-6 space-y-3 text-gray-light">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <span className="mt-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-black"><Check className="h-3 w-3" /></span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/signup">
                    <Button className="w-full font-bold uppercase tracking-wider shadow-neon-glow hover:scale-105 transition-all">Get Started</Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <MarketingFooter />
    </>
  );
}


